import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export function useQuotes() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getQuotes = useCallback(async ({ status, search, limit, userId, businessId } = {}) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('quotes')
        .select('*')
        .order('created_at', { ascending: false });

      if (userId) query = query.eq('user_id', userId);
      if (businessId) query = query.eq('business_id', businessId);
      if (status && status !== 'all') query = query.eq('status', status);
      if (limit) query = query.limit(limit);
      if (search) {
        query = query.or(
          `client_name.ilike.%${search}%,job_description.ilike.%${search}%`
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getQuoteById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)  // defense-in-depth: RLS + explicit user check
        .single();
      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createQuote = useCallback(async (quoteData) => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('quotes')
        .insert({
          ...quoteData,
          status: quoteData.status || 'draft',
          created_at: now,
          updated_at: now,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateQuote = useCallback(async (id, updates) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('quotes')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', user.id)  // defense-in-depth: RLS + explicit user check
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteQuote = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('quotes')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);  // defense-in-depth: RLS + explicit user check
      if (error) throw error;
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getQuoteStats = useCallback(async (userId, businessId) => {
    try {
      let query = supabase
        .from('quotes')
        .select('status, total, created_at')
        .eq('user_id', userId);
      if (businessId) query = query.eq('business_id', businessId);
      const { data, error } = await query;

      if (error) throw error;

      const quotes = data || [];
      const totalQuoted = quotes.reduce((sum, q) => sum + (q.total || 0), 0);
      const accepted = quotes.filter((q) => q.status === 'accepted');
      const pending = quotes.filter(
        (q) => q.status === 'draft' || q.status === 'sent'
      );
      const acceptedTotal = accepted.reduce((sum, q) => sum + (q.total || 0), 0);
      const acceptRate =
        quotes.length > 0 ? Math.round((accepted.length / quotes.length) * 100) : 0;

      // Monthly totals for last 6 months
      const now = new Date();
      const monthly = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const endDate = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
        const monthQuotes = accepted.filter((q) => {
          const d = new Date(q.created_at);
          return d >= date && d <= endDate;
        });
        monthly.push({
          month: date.toLocaleString('default', { month: 'short' }),
          total: monthQuotes.reduce((sum, q) => sum + (q.total || 0), 0),
        });
      }

      return {
        totalQuoted,
        acceptedCount: accepted.length,
        acceptedTotal,
        acceptRate,
        pendingCount: pending.length,
        totalCount: quotes.length,
        monthly,
      };
    } catch (err) {
      return {
        totalQuoted: 0,
        acceptedCount: 0,
        acceptedTotal: 0,
        acceptRate: 0,
        pendingCount: 0,
        totalCount: 0,
        monthly: [],
      };
    }
  }, []);

  return {
    loading,
    error,
    getQuotes,
    getQuoteById,
    createQuote,
    updateQuote,
    deleteQuote,
    getQuoteStats,
  };
}
